import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Button, IconButton, Notice } from "../../ui";
import { useNextT } from "../../i18n";
import { NEWS } from "../content";
import s from "./ShowMeStrip.module.scss";

/**
 * "Show me" in What's new opens the real page with one line on top (board 147): "New here: …" and Got it / ✕. No arrows, no tour, no
 * dimming: the person keeps working. The address carries `?hint=<item>`; it is taken off the address at once and the line lives
 * here until it is closed or the person goes to another page, so it never comes back for that item.
 */
export const ShowMeStrip = () => {
  const { t } = useNextT();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const [hint, setHint] = useState<{ id: string; path: string } | null>(null);
  const strip = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const params = new URLSearchParams(search);
    const id = params.get("hint");
    if (!id) return;
    params.delete("hint");
    const rest = params.toString();
    if (NEWS.some((n) => n.id === id)) setHint({ id, path: pathname });
    navigate({ pathname, search: rest ? `?${rest}` : "" }, { replace: true });
  }, [search, pathname, navigate]);

  // Another page: the line is for the page Show me opened.
  useEffect(() => { if (hint && hint.path !== pathname) setHint(null); }, [pathname, hint]);

  if (!hint) return null;
  const close = () => setHint(null);
  return (
    <div className={`cy-next ${s.wrap}`} ref={strip}>
      <Notice tone="info"
        action={<Button onClick={close}>{t("help.hint.got_it")}</Button>}
        dismiss={<IconButton label={t("help.hint.close")} icon={<CloseRoundedIcon />} onClick={close} />}>
        <strong>{t("help.hint.prefix")}</strong> {t(`help.news.${hint.id}.hint`)}
      </Notice>
    </div>
  );
};
